import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Text } from "@pentabd/ui";

import {
  InputTextVertical,
  SelectControllerVertical,
  SelectEmployee,
  VerticalInputFormLayout,
} from "@components";

import { useMetaData } from "@hooks/inventory/roomConfiguration/useMetaData";
import { useLanguage } from "@hooks/useLanguage";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import useActionCreator from "../../../../../../redux/hooks/useActionCreator";

import {
  getConvertedMetaData,
  getConvertedMetaDataForEmployee,
  getConvertedMetaDataForInventoryRoom,
  getConvertedOfficeData,
} from "@helpers/inventory/metaDataConvert";

import { ROOM_META_DATA } from "@constants/inventory/room-meta-data";
import { DeskCreateType, DeskDetailsResponseType } from "@type/inventory/desk";
import { getFloors } from "@helpers/inventory/getFloors";
import { GENERAL_INFO } from "@validations/HRM/employee/generalInfoValidation";

import { disabledRoomConfigDeskInput } from "@utils/roomConfigDeskDisabled";
import {
  ROOM_CONFIG_DESK,
  roomConfigBuildingValidationSchema,
} from "@validations/Inventory/roomConfig/roomConfigDeskValidation";
import { yupResolver } from "@hookform/resolvers/yup";
import { useOfficeList } from "@hooks/inventory/roomConfiguration/useOfficeList";
import { OfficeResponseType } from "@type/inventory/office";

const { BUILDING, ROOM_TYPE, ROOM } = ROOM_META_DATA;

interface DeskFormProps {
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
  setErrorMessage: (err: string) => void;
  deskState: any;
  updateDeskHandler: (data: DeskCreateType) => void;
  createDeskHandler: (data: DeskCreateType) => void;
}

// export const ROOM_CONFIG = FORM_FIELDS.INVENTORY.ROOM_CONFIG_DESK_TAB;

export default function DeskForm({
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
  setErrorMessage,
  deskState,
  updateDeskHandler,
  createDeskHandler,
}: DeskFormProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const method = useForm<DeskCreateType>({
    resolver: yupResolver(roomConfigBuildingValidationSchema),
  });
  const [floors, setFloors] = useState([]);
  const [deskId, setDeskId] = useState<number | string>("");

  const {
    getSingleData,
    createError,
    createSuccess,
    updateError,
    updateSuccess,
  } = deskState;

  const { data: deskDetails } =
    getSingleData || ({} as DeskDetailsResponseType);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = method;

  const { roomConfigOfficeState, setRefetch: handleOfficeRefetch } =
    useOfficeList(true);

  const { getData } = roomConfigOfficeState;

  const { data: offices } = getData || ({} as OfficeResponseType);

  const { data: buildings, handleQuery: handleBuildingQuery } =
    useMetaData(BUILDING);
  const { data: roomTypes } = useMetaData(ROOM_TYPE);
  const { data: rooms, setQuery: setRoomQueries } = useMetaData(ROOM);

  const { createDesk } = useActionCreator();

  const resetAction = () => {
    reset();
    setDeskId("");
  };

  const onSubmit: SubmitHandler<DeskCreateType> = (data) => {
    if (!data.pabxNumber) {
      data.pabxNumber = 0;
    }
    if (deskDetails) {
      updateDeskHandler({
        ...data,
        floor: Number(data.floor),
        deskNumber: Number(data.deskNumber),
        pabxNumber: Number(data.pabxNumber),
      });
    } else {
      createDesk({
        ...data,
        floor: Number(data.floor),
        deskNumber: Number(data.deskNumber),
        pabxNumber: data.pabxNumber ? Number(data.pabxNumber) : 0,
      });
    }
  };

  useEffect(() => {
    if (createSuccess | updateSuccess) {
      reset();
      setIsSuccessModalOpen(createSuccess || updateSuccess);
      if (updateSuccess) {
        setDeskId("");
      }
    }
    setIsErrorModalOpen(createError || updateError);

    if (createError?.response && createError?.response?.data) {
      setErrorMessage(
        createError.response.data.message || "something was wrong",
      );
    }

    if (updateError?.response && updateError?.response?.data) {
      setErrorMessage(
        updateError.response.data.message || "something was wrong",
      );
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    reset,
    setIsErrorModalOpen,
    setIsSuccessModalOpen,
    setErrorMessage,
  ]);

  const watchOfficeLocation = watch("office");
  const watchBuilding = watch("building");
  const watchFloor = watch("floor");
  const watchRoomType = watch("roomType");
  const watchRoom = watch("room");

  useEffect(() => {
    if (watchOfficeLocation) {
      handleBuildingQuery("officeId", watchOfficeLocation);
    }
  }, [watch, watchOfficeLocation]);

  useEffect(() => {
    if (watchBuilding && buildings) {
      const updatedFloors = getFloors(watchBuilding, buildings);
      setFloors(updatedFloors as any);
    }
  }, [watch, watchBuilding, buildings]);

  useEffect(() => {
    if (watchRoomType && watchBuilding && buildings) {
      setRoomQueries({
        buildingId: watchBuilding,
        roomTypeId: watchRoomType,
      });
    }
  }, [watch, watchRoomType]);

  useEffect(() => {
    if (deskDetails) {
      // refetching data
      if (deskDetails?.office?.id === watchOfficeLocation) {
        handleBuildingQuery("officeId", deskDetails?.office?.id);
        const updatedFloors = getFloors(deskDetails?.building?.id, buildings);
        setFloors(updatedFloors as any);
        setRoomQueries({
          buildingId: deskDetails?.building?.id,
          roomTypeId: deskDetails?.roomType?.id,
        });
      } else {
        handleOfficeRefetch && handleOfficeRefetch({ refetch: true });
      }

      setValue("office", deskDetails?.office?.id);
      setValue("building", deskDetails?.building?.id);
      setValue("floor", deskDetails?.floor);
      setValue("roomType", deskDetails?.roomType?.id);
      setValue("room", deskDetails?.room?.id);
      setValue("type", deskDetails?.type);
      setValue("deskNumber", deskDetails?.deskNumber);
      setValue("pabxNumber", deskDetails?.pabxNumber);
      setValue(
        `${GENERAL_INFO.WING_DESK_IO_E0}-assignedEmployee`,
        deskDetails?.assignedEmployee?.unitId,
      );
      setValue(
        `${GENERAL_INFO.DESIGNATION}-assignedEmployee`,
        deskDetails?.assignedEmployee?.positionId,
      );
      setValue("assignedEmployee", deskDetails?.assignedEmployee?.id);
      setDeskId(deskDetails.id);
    }
  }, [deskDetails, setValue]);

  useEffect(() => resetAction(), []);

  return (
    <FormProvider {...method}>
      <VerticalInputFormLayout
        label="INVENTORY_ROOM_CONFIG.DESK.FORM.HEADER"
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        noMargin
        isUpdate={deskId ? true : false}
        cancelAction={resetAction}
      >
        <SelectControllerVertical
          label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.OFFICE"
          name={ROOM_CONFIG_DESK.OFFICE}
          register={{
            ...register(ROOM_CONFIG_DESK.OFFICE),
          }}
          selectAction={() => {
            disabledRoomConfigDeskInput(ROOM_CONFIG_DESK.OFFICE, setValue);
          }}
          options={getConvertedOfficeData(offices, language)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
        />
        <SelectControllerVertical
          label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.BUILDING"
          name={ROOM_CONFIG_DESK.BUILDING}
          register={{
            ...register(ROOM_CONFIG_DESK.BUILDING),
          }}
          selectAction={() => {
            disabledRoomConfigDeskInput(ROOM_CONFIG_DESK.BUILDING, setValue);
          }}
          options={getConvertedMetaData(buildings, language)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
          disabled={watchOfficeLocation ? false : true}
        />
        <SelectControllerVertical
          label="INVENTORY_ROOM_CONFIG.ROOM.FORM.FIELDS.FLOOR"
          name={ROOM_CONFIG_DESK.FLOOR}
          register={{
            ...register(ROOM_CONFIG_DESK.FLOOR),
          }}
          selectAction={() => {
            disabledRoomConfigDeskInput(ROOM_CONFIG_DESK.FLOOR, setValue);
          }}
          options={floors}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
          disabled={watchBuilding ? false : true}
        />
        <SelectControllerVertical
          label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.ROOM_TYPE"
          name={ROOM_CONFIG_DESK.ROOM_TYPE}
          register={{
            ...register(ROOM_CONFIG_DESK.ROOM_TYPE),
          }}
          selectAction={() => {
            disabledRoomConfigDeskInput(ROOM_CONFIG_DESK.ROOM_TYPE, setValue);
          }}
          options={getConvertedMetaDataForInventoryRoom(roomTypes, language)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
          disabled={watchFloor ? false : true}
        />
        <SelectControllerVertical
          label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.ROOM"
          name={ROOM_CONFIG_DESK.ROOM}
          register={{
            ...register(ROOM_CONFIG_DESK.ROOM),
          }}
          selectAction={() => {
            disabledRoomConfigDeskInput(ROOM_CONFIG_DESK.ROOM, setValue);
          }}
          options={getConvertedMetaDataForEmployee(rooms, language, true)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
          disabled={watchRoomType ? false : true}
        />
        <SelectControllerVertical
          label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.TYPE"
          name={ROOM_CONFIG_DESK.TYPE}
          register={{
            ...register(ROOM_CONFIG_DESK.TYPE),
          }}
          options={[
            {
              label: "Desk",
              value: 1,
            },
            {
              label: "Bed",
              value: 2,
            },
          ]}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
          disabled={watchRoom ? false : true}
        />
        <InputTextVertical
          label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.DESK_NO"
          type="number"
          register={{
            ...register(ROOM_CONFIG_DESK.DESK_NUMBER),
          }}
          error={errors}
          required
          cols={3}
        />
        <InputTextVertical
          label="INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.PABX_NO"
          // type="number"
          register={{ ...register(ROOM_CONFIG_DESK.PABX_NUMBER) }}
          // register={{
          //   ...register(ROOM_CONFIG_DESK.PABX_NUMBER, {
          //     valueAsNumber: true,
          //     max: {
          //       value: 9999,
          //       message: "PABX number cannot have more than 4 digits",
          //     },
          //     min: {
          //       value: 0,
          //       message: "PABX number cannot be negative",
          //     },
          //   }),
          // }}
          error={errors}
          cols={3}
        />
        <div className="col-12">
          <Text
            weight="semibold"
            color="title"
            size="sm"
            component="p"
            className="mb-3"
          >
            {t("INVENTORY_ROOM_CONFIG.DESK.FORM.FIELDS.EMPLOYEE.LABEL")}
            <span className="text-danger">&#42;</span>
          </Text>
          <SelectEmployee
            selectSingleEmployee
            registeredName="assignedEmployee"
            cols={3}
          />
        </div>
      </VerticalInputFormLayout>
    </FormProvider>
  );
}
